(function () {
  if (customElements.get('newsletter-panel')) return;

  class NewsletterPanel extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.dismissed = false;
      this.frame = 0;
      this.threshold = Math.min(100, Math.max(0, Number(this.dataset.scrollThreshold) || 25));
      this.frequency = this.dataset.frequency || 'no_limit';
      this.storageKey = this.dataset.storageKey || 'axiom_newsletter_panel';
      this.mobileMedia = window.matchMedia('(max-width: 767px)');
      this.trigger = document.querySelector(`[data-newsletter-panel-trigger="${this.dataset.sectionId}"]`);
      this.tabShell = document.querySelector(`[data-newsletter-panel-tab-shell="${this.dataset.sectionId}"]`);
      this.tabDismissButton = this.tabShell?.querySelector('[data-newsletter-panel-tab-dismiss]');
      this.tabDismissed = false;

      this.closeButton = this.querySelector('[data-newsletter-panel-close]');
      this.backdrop = this.querySelector('[data-newsletter-panel-backdrop]');
      this.surface = this.querySelector('.newsletter-panel__surface');
      this.prepareImageLayout();
      this.closeButton?.addEventListener('click', () => this.close(), { signal: this.abortController.signal });
      this.backdrop?.addEventListener('click', () => this.close(), { signal: this.abortController.signal });
      this.trigger?.addEventListener('click', () => this.open({ manual: true, recordFrequency: false }), { signal: this.abortController.signal });
      this.tabDismissButton?.addEventListener('click', () => this.dismissTab(), { signal: this.abortController.signal });
      document.addEventListener('theme:drawer:close', (event) => {
        if (event.detail?.drawer !== this) return;
        this.dismissed = true;
        this.updateTrigger(false);
      }, { signal: this.abortController.signal });
      this.updateTrigger(false);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize, { passive: true, signal: this.abortController.signal });

      if (this.dataset.designMode === 'true' || this.querySelector('[data-newsletter-panel-open-immediately]')) {
        this.open({ recordFrequency: false });
        return;
      }

      if (this.isFrequencyLimited()) return;

      window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.abortController.signal });
      this.onScroll();
    }

    disconnectedCallback() {
      if (this.classList.contains('is-open')) this.close({ preserveDismissedState: true });
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
    }

    prepareImageLayout() {
      const blocks = this.querySelector('[data-newsletter-panel-blocks]');
      if (!blocks) return;
      const image = Array.from(blocks.children).find((child) => child.matches('[data-newsletter-panel-image]'));
      if (!image) return;

      const position = image.dataset.newsletterImagePosition || 'top';
      const content = document.createElement('div');
      content.className = 'newsletter-panel__content';
      Array.from(blocks.childNodes).forEach((node) => {
        if (node !== image) content.appendChild(node);
      });
      if (this.closeButton) content.prepend(this.closeButton);
      blocks.append(image, content);
      blocks.classList.add('has-positioned-image');
      this.dataset.imagePosition = position;
      this.imageBlock = image;
      this.contentPanel = content;

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleImageLayout());
        this.resizeObserver.observe(content);
      }
    }

    scheduleImageLayout() {
      if (!this.imageBlock || this.layoutFrame) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = 0;
        this.updateImageLayout();
      });
    }

    updateImageLayout() {
      if (!this.imageBlock || !this.contentPanel || !this.surface || this.hidden) return;

      const ratio = Math.max(0.1, Number(this.imageBlock.dataset.newsletterMediaRatio) || 1.777777);
      const position = this.dataset.imagePosition || 'top';
      const isMobile = this.mobileMedia.matches;
      const isSide = !isMobile && (position === 'left' || position === 'right');
      const imageIsVisible = window.getComputedStyle(this.imageBlock).display !== 'none';
      const surfaceStyles = window.getComputedStyle(this.surface);
      const panelStyles = window.getComputedStyle(this);
      const pageStyles = window.getComputedStyle(document.documentElement);
      const pageGutter = Number.parseFloat(pageStyles.getPropertyValue('--page-gutter')) || 24;
      const surfaceInset = isMobile ? 12 : Math.max(16, pageGutter);
      const configuredContentWidth = Number.parseFloat(panelStyles.getPropertyValue('--newsletter-panel-width')) || 420;
      const basePanelWidth = Number.parseFloat(panelStyles.getPropertyValue('--newsletter-panel-base-width')) || 300;
      const availableSurfaceWidth = Math.max(1, window.innerWidth - (surfaceInset * 2));
      const maximumHeight = Math.max(160, Number.parseFloat(surfaceStyles.maxHeight) || (window.innerHeight - 32));
      let contentWidth = Math.max(1, Math.min(configuredContentWidth, availableSurfaceWidth));
      const naturalContentHeight = Math.max(1, this.contentPanel.scrollHeight);
      let contentHeight = Math.min(naturalContentHeight, maximumHeight);
      let imageWidth = contentWidth;
      // Preserve the selected ratio at Small; larger sizes grow 4px wider for every 1px taller.
      const getScaledMediaHeight = (width) => {
        const baseMediaWidth = isSide ? basePanelWidth * (2 / 3) : basePanelWidth;
        const baseWidth = Math.min(width, baseMediaWidth);
        return (baseWidth / ratio) + (Math.max(0, width - baseWidth) * 0.25);
      };
      let imageHeight = getScaledMediaHeight(imageWidth);
      let expandedWidth = contentWidth;

      if (!imageIsVisible) {
        imageWidth = 0;
        imageHeight = 0;
      } else if (isSide) {
        /* Flyout size owns the side-by-side width. Media ratio may change the
           shared height, but must not resize the flyout or squeeze its content. */
        expandedWidth = Math.min(configuredContentWidth * 2, availableSurfaceWidth);
        imageWidth = Math.max(1, expandedWidth / 3);
        contentWidth = Math.max(1, expandedWidth - imageWidth);
        contentHeight = Math.min(Math.max(naturalContentHeight, getScaledMediaHeight(imageWidth)), maximumHeight);
        imageHeight = contentHeight;
      } else if (contentHeight + imageHeight > maximumHeight) {
        contentHeight = Math.max(120, maximumHeight - imageHeight);
      }

      this.style.setProperty('--newsletter-content-width', `${contentWidth}px`);
      this.style.setProperty('--newsletter-content-height', `${contentHeight}px`);
      this.style.setProperty('--newsletter-image-width', `${imageWidth}px`);
      this.style.setProperty('--newsletter-image-height', `${imageHeight}px`);
      this.style.setProperty('--newsletter-expanded-width', `${expandedWidth}px`);
    }

    onScroll() {
      if (this.isHiddenOnMobile() || this.frame || this.dismissed || this.classList.contains('is-open')) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        const scrollableDistance = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollableDistance <= 0) return;
        const progress = (window.scrollY / scrollableDistance) * 100;
        if (progress >= this.threshold) this.open();
      });
    }

    onResize() {
      this.onScroll();
      this.scheduleImageLayout();
    }

    open(options) {
      if (options?.manual) this.dismissed = false;
      if (this.isHiddenOnMobile() || this.dismissed || this.classList.contains('is-open')) return;
      this.hidden = false;
      this.updateImageLayout();
      if (window.Axiom && typeof window.Axiom.openDrawer === 'function') {
        window.Axiom.openDrawer(this, this.backdrop, {
          autofocus: Boolean(options?.manual),
          focusTarget: this.closeButton,
          returnFocus: options?.manual ? this.trigger : null,
          trapFocus: true
        });
      } else {
        this.hidden = false;
        if (this.backdrop) this.backdrop.hidden = false;
        this.classList.add('is-open');
        this.backdrop?.classList.add('is-open');
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('is-scroll-locked');
      }
      this.updateTrigger(true);
      if (options?.recordFrequency !== false) this.recordFrequency();
    }

    isHiddenOnMobile() {
      return this.dataset.hideOnMobile === 'true' && this.mobileMedia.matches;
    }

    close(options) {
      if (!this.classList.contains('is-open')) return;
      if (!options?.preserveDismissedState) this.dismissed = true;
      if (window.Axiom && typeof window.Axiom.closeDrawer === 'function') {
        window.Axiom.closeDrawer(this, this.backdrop);
      } else {
        this.classList.remove('is-open');
        this.backdrop?.classList.remove('is-open');
        this.setAttribute('inert', '');
        this.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('is-scroll-locked');
        this.hidden = true;
        if (this.backdrop) this.backdrop.hidden = true;
      }
      this.updateTrigger(false);
    }

    updateTrigger(isOpen) {
      if (!this.trigger) return;
      if (this.tabShell) this.tabShell.hidden = isOpen || this.tabDismissed;
      this.trigger.setAttribute('aria-expanded', String(isOpen));
    }

    dismissTab() {
      this.tabDismissed = true;
      this.updateTrigger(false);
    }

    isFrequencyLimited() {
      try {
        if (this.frequency === 'once_per_session') {
          return sessionStorage.getItem(this.storageKey) === '1';
        }

        const ageByFrequency = {
          every_7_days: 7 * 24 * 60 * 60 * 1000,
          every_30_days: 30 * 24 * 60 * 60 * 1000
        };
        const maximumAge = ageByFrequency[this.frequency];
        if (!maximumAge) return false;
        const shownAt = Number(localStorage.getItem(this.storageKey));
        return shownAt > 0 && Date.now() - shownAt < maximumAge;
      } catch (error) {
        return false;
      }
    }

    recordFrequency() {
      try {
        if (this.frequency === 'once_per_session') {
          sessionStorage.setItem(this.storageKey, '1');
          return;
        }
        if (this.frequency === 'every_7_days' || this.frequency === 'every_30_days') {
          localStorage.setItem(this.storageKey, String(Date.now()));
        }
      } catch (error) {
        // Storage can be unavailable in privacy modes; the panel still works per page.
      }
    }
  }

  customElements.define('newsletter-panel', NewsletterPanel);
})();
